import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'

import Container from '@mui/material/Container'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Link from '@mui/material/Link'

import { Invite, CreateUser, ValidationError } from '../types/signup'
import { login, get, post, setPageTitle } from '../utils/service'
import Alert from '../components/alert/alert'

const SignupView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [hasValidInvite, setHasValidInvite] = React.useState<boolean>(false)
  const [isPosting, setIsPosting] = React.useState<boolean>(false)
  const [customerName, setCustomerName] = React.useState<string>('')
  const [customerNameError, setCustomerNameError] = React.useState<string>('')
  const [username, setUsername] = React.useState<string>('')
  const [usernameError, setUsernameError] = React.useState<string>('')
  const [userEmail, setUserEmail] = React.useState<string>('')
  const [userEmailError, setUserEmailError] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [passwordError, setPasswordError] = React.useState<string>('')
  const [passwordAgain, setPasswordAgain] = React.useState<string>('')
  const [passwordAgainError, setPasswordAgainError] = React.useState<string>('')
  const [machineName, setMachineName] = React.useState<string>('')
  const [machineCount, setMachineCount] = React.useState<number>(0)
  const [loginError, setLoginError] = React.useState<string>('')

  const updateFormValidationErrors = (errors: ValidationError): void => {
    if (errors.user?.username) {
      setUsernameError(errors.user.username)
    }
    if (errors.user?.email) {
      setUserEmailError(errors.user.email)
    }
    if (errors.user?.password) {
      setPasswordError(errors.user.password)
    }
    if (errors.customer?.name) {
      setCustomerNameError(errors.customer.name)
    }
  }

  const doLogin = (invite: Invite): void => {
    if (invite.username) {
      setIsLoading(true)
      login(
        invite.username,
        password,
        () => {
          setIsLoading(false)
          window.location.href = '/' // hard navigate to load data
        },
        (statusCode: number | undefined, invalidGrant: boolean, noResponse: boolean, baseUrl: string | undefined) => {
          setIsLoading(false)
          if ((statusCode === 400 || statusCode === 401) && invalidGrant) {
            setLoginError(t('loginview.unauthorized'))
          } else if (statusCode) {
            setLoginError(t('loginview.unexpectedresponse', { statusCode: statusCode }))
          } else if (noResponse && baseUrl) {
            setLoginError(t('loginview.couldnotcontact', { baseUrl: baseUrl }))
          } else {
            setLoginError(t('loginview.genericerror'))
          }
        }
      )
    }
  }
  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setIsPosting(true)
    post<CreateUser, Invite>(`/invites/${params.inviteId}/create_user/`, {
      customer: {
        name: customerName,
      },
      user: {
        username: username,
        email: userEmail,
        password: password,
      },
    })
      .then((response) => {
        setIsPosting(false)
        doLogin(response.data)
      })
      .catch((err) => {
        setIsPosting(false)
        if (err.response && err.response.status === 400) {
          updateFormValidationErrors(err.response.data)
        }
      })
  }

  React.useEffect(() => {
    setPageTitle(t('signup.pagetitle'))
  }, [t])

  React.useEffect(() => {
    setIsLoading(true)
    get<Invite>(`/invites/${params.inviteId}/`)
      .then((response) => {
        setIsLoading(false)
        setMachineCount(response.data.machines.length)
        setMachineName(response.data.machines.map((x) => x.machine_name).join(', '))
        setHasValidInvite(true)
      })
      .catch((err) => {
        setIsLoading(false)
        setHasValidInvite(false)
      })
  }, [params.inviteId, setMachineName])

  React.useEffect(() => {
    if (password) {
      setPasswordError('')
      setPasswordAgainError('')

      if (passwordAgain && password !== passwordAgain) {
        setPasswordAgainError(t('signup.errorpasswordsdonotmatch'))
      }
    }
  }, [t, password, passwordAgain])

  const supportLink = <Link href="/support/ticket">"support"</Link>

  return (
    <Container component="main" maxWidth="md">
      {isLoading && <CircularProgress />}
      {loginError && <Alert severity="error">{loginError}</Alert>}
      {!isLoading && !hasValidInvite && (
        <Alert severity="error">
          <Trans i18nKey="signup.invalidinvite" components={{ supportLink }}></Trans>
        </Alert>
      )}
      {!isLoading && hasValidInvite && (
        <form onSubmit={onFormSubmit}>
          <Stack spacing={3}>
            <Typography variant="h4" component="h1">
              {t('signup.createformachine', { count: machineCount, machineName })}
            </Typography>
            <TextField
              fullWidth
              label={t('signup.customername')}
              variant="outlined"
              helperText={customerNameError ? customerNameError : null}
              error={!!customerNameError}
              required
              value={customerName}
              inputProps={{
                autoCapitalize: 'off',
                autoCorrect: 'off',
              }}
              onChange={(event) => {
                setCustomerName(event.target.value)
                setCustomerNameError('')
              }}
            />
            <TextField
              fullWidth
              label={t('signup.useremail')}
              variant="outlined"
              helperText={userEmailError ? userEmailError : null}
              error={!!userEmailError}
              required
              value={userEmail}
              onChange={(event) => {
                setUserEmail(event.target.value)
                setUserEmailError('')
              }}
            />
            <TextField
              fullWidth
              label={t('signup.username')}
              variant="outlined"
              helperText={usernameError ? usernameError : null}
              error={!!usernameError}
              autoComplete="off"
              required
              value={username}
              onChange={(event) => {
                setUsername(event.target.value)
                setUsernameError('')
              }}
            />
            <TextField
              fullWidth
              label={t('signup.password')}
              variant="outlined"
              type="password"
              helperText={passwordError ? passwordError : null}
              error={!!passwordError}
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
              }}
            />
            <TextField
              fullWidth
              label={t('signup.passwordagain')}
              variant="outlined"
              type="password"
              helperText={passwordAgainError ? passwordAgainError : null}
              error={!!passwordAgainError}
              required
              value={passwordAgain}
              onChange={(event) => {
                setPasswordAgain(event.target.value)
              }}
            />
            <LoadingButton type="submit" variant="contained" loading={isPosting}>
              {t('signup.create')}
            </LoadingButton>
          </Stack>
        </form>
      )}
    </Container>
  )
}

export default SignupView
